// Client Projects tab. Lists this client's projects + inline + Project /
// + Estimate / + Presentation creation flows. Ported from the original
// ClientDetailPage Projects <TabsContent> block (Phase 7.5.5 + nav
// updates from the 2026-05-29 refactor).
//
// Per-project actions:
//   - + Estimate creates an `orders` row with statusStage='quote' (the
//     line-editable estimate). Navigates into the project sub-nav at
//     /projects/:id/estimate/:orderId so the project context is preserved
//     (2026-05-29 — no global /orders/:id surface for staff).
//   - + Presentation creates a presentation draft and navigates to its
//     builder.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderKanban } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";

interface ProjectRow {
  id: string;
  projectNumber: number;
  name: string;
  statusStage: string;
  statusState: string;
  inHandsDate: string;
  propertyName: string | null;
}

function projectStageTone(
  stage: string,
):
  | "active"
  | "prospect"
  | "dormant"
  | "closed"
  | "info" {
  switch (stage) {
    case "opportunity":
    case "presentation":
    case "estimate":
      return "prospect";
    case "sales_order":
    case "in_production":
    case "shipped":
      return "active";
    case "invoiced":
      return "info";
    case "closed":
    case "lost":
      return "closed";
    default:
      return "info";
  }
}

export function ClientProjectsTab() {
  const { canEdit, clientId } = useOutletContext<ClientDetailContext>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["clients", clientId, "projects"],
    queryFn: () =>
      api<{ rows: ProjectRow[] }>(
        `/projects?client_id=${clientId}&limit=100`,
      ),
    enabled: clientId.length > 0,
  });

  const projects = data?.rows ?? [];

  // No default in-hands date — the rep picks it (Stephanie 2026-11-02: it
  // defaulted to a month out; "the sales rep needs to manually choose").
  const defaultInHandsDate = "";

  // "+ Project" on the account page opens straight into this form (?new=1, Stephanie 2026-11-02).
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreatingProject, setIsCreatingProject] = useState(() => searchParams.get("new") === "1");
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectInHandsDate, setNewProjectInHandsDate] = useState(
    defaultInHandsDate,
  );
  const [newProjectError, setNewProjectError] = useState<string | null>(null);

  const createProjectMutation = useMutation({
    mutationFn: async (body: { name: string; in_hands_date: string }) =>
      api<{ project: { id: string } }>("/projects", {
        method: "POST",
        body: JSON.stringify({ client_id: clientId, ...body }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "projects"],
      });
      setIsCreatingProject(false);
      setNewProjectName("");
      setNewProjectInHandsDate(defaultInHandsDate);
      setNewProjectError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const body = err.body as { error?: string } | null;
        setNewProjectError(body?.error ?? "Couldn't create project.");
      } else {
        setNewProjectError("Couldn't create project. Try again.");
      }
    },
  });

  const [creatingOrderForProjectId, setCreatingOrderForProjectId] = useState<
    string | null
  >(null);
  const [newOrderInHandsDate, setNewOrderInHandsDate] = useState(
    defaultInHandsDate,
  );
  const [newOrderError, setNewOrderError] = useState<string | null>(null);

  const createOrderMutation = useMutation({
    mutationFn: async (body: { project_id: string; in_hands_date: string }) =>
      api<{ order: { id: string } }>("/orders", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (data, variables) => {
      // The project's lists and counts, and this account's projects (Stephanie 2026-11-02 sweep).
      void queryClient.invalidateQueries({ queryKey: ["merchd.projectDetail"] });
      void queryClient.invalidateQueries({ queryKey: ["project", variables.project_id] });
      void queryClient.invalidateQueries({ queryKey: ["clients", clientId, "projects"] });
      // Project-only access (2026-05-29): navigate to the order INSIDE
      // its project's sub-nav, not the standalone /orders/:id route.
      navigate(
        `/projects/${variables.project_id}/quotes/${data.order.id}`,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const body = err.body as { error?: string } | null;
        setNewOrderError(body?.error ?? "Couldn't create order.");
      } else {
        setNewOrderError("Couldn't create order. Try again.");
      }
    },
  });

  const [newPresentationError, setNewPresentationError] = useState<
    string | null
  >(null);
  const createPresentationMutation = useMutation({
    mutationFn: async (body: { project_id: string }) =>
      api<{ presentation: { id: string } }>("/presentations", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["merchd.projectDetail"] });
      void queryClient.invalidateQueries({ queryKey: ["project", variables.project_id] });
      void queryClient.invalidateQueries({ queryKey: ["clients", clientId, "projects"] });
      // Land in the new presentation's project sub-nav (2026-06-01 nav
      // follow-up) so the project context is preserved on first open.
      navigate(
        `/projects/${variables.project_id}/presentation/${data.presentation.id}`,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const body = err.body as { error?: string } | null;
        setNewPresentationError(body?.error ?? "Couldn't create presentation.");
      } else {
        setNewPresentationError("Couldn't create presentation. Try again.");
      }
    },
  });

  function submitNewProject(e: React.FormEvent): void {
    e.preventDefault();
    const trimmed = newProjectName.trim();
    if (trimmed.length === 0) {
      setNewProjectError("Opportunity name is required.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newProjectInHandsDate)) {
      setNewProjectError("In-hands date is required.");
      return;
    }
    setNewProjectError(null);
    createProjectMutation.mutate({
      name: trimmed,
      in_hands_date: newProjectInHandsDate,
    });
  }

  function startNewOrder(projectId: string): void {
    setCreatingOrderForProjectId(projectId);
    // The opportunity's own in-hands date (Stephanie 2026-10-26: "the in
    // hands date i already picked has changed"); 30 days out only if none.
    const own = projects.find((x) => x.id === projectId)?.inHandsDate?.slice(0, 10);
    setNewOrderInHandsDate(own && /^\d{4}-\d{2}-\d{2}$/.test(own) ? own : defaultInHandsDate);
    setNewOrderError(null);
  }

  function cancelNewOrder(): void {
    setCreatingOrderForProjectId(null);
    setNewOrderError(null);
  }

  function submitNewOrder(e: React.FormEvent, projectId: string): void {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newOrderInHandsDate)) {
      setNewOrderError("In-hands date is required.");
      return;
    }
    setNewOrderError(null);
    createOrderMutation.mutate({
      project_id: projectId,
      in_hands_date: newOrderInHandsDate,
    });
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brown-500">
          Projects <span className="text-brown-300">({projects.length})</span>
        </h2>
        {canEdit && !isCreatingProject && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setIsCreatingProject(true);
              setNewProjectName("");
              setNewProjectInHandsDate(defaultInHandsDate);
              setNewProjectError(null);
            }}
          >
            + Project
          </Button>
        )}
      </div>

      {isCreatingProject && (
        <form
          onSubmit={submitNewProject}
          className="mb-4 rounded-lg border border-peach-300 bg-peach-50 p-5"
        >
          <div className="mb-3 font-semibold text-brown-500">New opportunity</div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                Name
              </span>
              <Input
                autoFocus
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                maxLength={200}
                placeholder="e.g. Summer 2026 swag drop"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                In-hands date
              </span>
              <Input
                type="date"
                className="sm:w-44"
                value={newProjectInHandsDate}
                onChange={(e) => setNewProjectInHandsDate(e.target.value)}
              />
            </label>
          </div>
          {newProjectError && (
            <div className="mt-3 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-500">
              {newProjectError}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={createProjectMutation.isPending}>
              {createProjectMutation.isPending
                ? "Creating…"
                : "Create opportunity"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsCreatingProject(false);
                setNewProjectName("");
                setNewProjectError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-sm text-brown-300">Loading projects…</div>
      ) : projects.length === 0 ? (
        !isCreatingProject && (
          <div className="rounded-lg border border-dashed border-brown-100 bg-white px-6 py-12 text-center text-sm text-brown-300">
            No projects yet.
          </div>
        )
      ) : (
        <ul className="divide-y divide-brown-50 rounded-lg border border-brown-100 bg-white">
          {projects.map((p) => (
            <li key={p.id} className="px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <Link
                  to={`/projects/${p.id}`}
                  className="flex flex-1 items-start gap-3 transition-colors hover:text-brown-500"
                >
                  <FolderKanban className="mt-0.5 h-4 w-4 text-brown-300" />
                  <div>
                    <div className="font-semibold text-brown-500 underline-offset-4 hover:underline">
                      {p.name}
                    </div>
                    <div className="text-xs text-brown-300">
                      #{p.projectNumber}
                      {p.propertyName && ` · ${p.propertyName}`}
                      {" · in hands "}
                      {/* The calendar day as picked (a bare date read as UTC shows a day early in Florida). */}
                      {new Date(`${p.inHandsDate.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-3">
                  <StatusPill tone={projectStageTone(p.statusStage)}>
                    {p.statusStage.replace(/_/g, " ")}
                  </StatusPill>
                  {/* Presentation first — the normal order (Stephanie 2026-10-26). */}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={createPresentationMutation.isPending}
                      onClick={() => {
                        setNewPresentationError(null);
                        createPresentationMutation.mutate({
                          project_id: p.id,
                        });
                      }}
                    >
                      {createPresentationMutation.isPending
                        ? "Creating…"
                        : "+ Presentation"}
                    </Button>
                  )}
                  {canEdit && creatingOrderForProjectId !== p.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startNewOrder(p.id)}
                      title="Start a new quote on this opportunity."
                    >
                      + Quote
                    </Button>
                  )}
                </div>
              </div>

              {newPresentationError &&
                createPresentationMutation.variables?.project_id === p.id && (
                  <div className="mt-3 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-500">
                    {newPresentationError}
                  </div>
                )}

              {creatingOrderForProjectId === p.id && (
                <form
                  onSubmit={(e) => submitNewOrder(e, p.id)}
                  className="mt-4 rounded-md border border-peach-300 bg-peach-50 p-4"
                >
                  <div className="mb-3 text-sm font-semibold text-brown-500">
                    New quote for {p.name}
                  </div>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
                      In-hands date
                    </span>
                    <Input
                      type="date"
                      className="sm:w-44"
                      value={newOrderInHandsDate}
                      onChange={(e) =>
                        setNewOrderInHandsDate(e.target.value)
                      }
                    />
                  </label>
                  {newOrderError && (
                    <div className="mt-3 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-500">
                      {newOrderError}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={createOrderMutation.isPending}
                    >
                      {createOrderMutation.isPending
                        ? "Creating…"
                        : "Create quote"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={cancelNewOrder}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
